/**
 * Roles and Permissions Hook
 *
 * Provides access to role definitions and permissions from the backend API.
 * Includes query hooks for reading and mutation hooks for CRUD operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type PermissionCategory =
  | 'dashboard'
  | 'operations'
  | 'customers'
  | 'inventory'
  | 'communication'
  | 'reports'
  | 'hr'
  | 'administration';

export interface Permission {
  id: string;
  nameKey: string;
  descKey: string;
  category: PermissionCategory;
  resource: string;
  action: string;
}

export interface Role {
  id: string;
  name: string;
  description: string | null;
  level: number;
  isSystem: boolean;
  isActive: boolean;
  permissionIds: string[];
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

const ROLES_QUERY_KEY = ['roles'];

/**
 * Get all available permissions
 */
export function usePermissions() {
  return useQuery({
    queryKey: [...ROLES_QUERY_KEY, 'permissions'],
    queryFn: async () => {
      const response = await api.get<{ data: Permission[] }>('/roles/permissions');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get permissions grouped by category
 */
export function usePermissionsByCategory() {
  return useQuery({
    queryKey: [...ROLES_QUERY_KEY, 'permissions', 'by-category'],
    queryFn: async () => {
      const response = await api.get<{ data: Record<PermissionCategory, Permission[]> }>(
        '/roles/permissions/by-category'
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get all role definitions
 */
export function useRoleDefinitions() {
  return useQuery({
    queryKey: [...ROLES_QUERY_KEY, 'definitions'],
    queryFn: async () => {
      const response = await api.get<{ data: Role[] }>('/roles/definitions');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Tenant roles for add/edit member dialogs (`GET /roles/definitions`). */
export function useRoles() {
  return useRoleDefinitions();
}

/**
 * Get single role definition with full permission details
 */
export function useRoleDefinition(roleId: string | undefined) {
  return useQuery({
    queryKey: [...ROLES_QUERY_KEY, 'definitions', roleId],
    queryFn: async () => {
      if (!roleId) return null;
      const response = await api.get<{ data: Role }>(`/roles/definitions/${roleId}`);
      return response.data;
    },
    enabled: !!roleId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get user counts per role
 */
export function useRoleUserCounts() {
  return useQuery({
    queryKey: [...ROLES_QUERY_KEY, 'user-counts'],
    queryFn: async () => {
      const response = await api.get<{ data: Record<string, number> }>(
        '/roles/user-counts'
      );
      return response.data;
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Combined hook for role management UI
 */
export function useRoleManagement() {
  const permissionsQuery = usePermissions();
  const permissionsByCategoryQuery = usePermissionsByCategory();
  const roleDefinitionsQuery = useRoleDefinitions();
  const userCountsQuery = useRoleUserCounts();

  const roles = roleDefinitionsQuery.data ?? [];
  const userCounts = userCountsQuery.data ?? {};

  const rolesWithCounts: Role[] = roles.map((role) => ({
    ...role,
    userCount: userCounts[role.id] ?? 0,
  }));

  return {
    permissions: permissionsQuery.data ?? [],
    permissionsByCategory: permissionsByCategoryQuery.data ?? ({} as Record<PermissionCategory, Permission[]>),
    roles: rolesWithCounts,
    isLoading:
      permissionsQuery.isLoading ||
      permissionsByCategoryQuery.isLoading ||
      roleDefinitionsQuery.isLoading ||
      userCountsQuery.isLoading,
    isError:
      permissionsQuery.isError ||
      permissionsByCategoryQuery.isError ||
      roleDefinitionsQuery.isError ||
      userCountsQuery.isError,
    error:
      permissionsQuery.error ||
      permissionsByCategoryQuery.error ||
      roleDefinitionsQuery.error ||
      userCountsQuery.error,
    refetch: () => {
      void roleDefinitionsQuery.refetch();
      void userCountsQuery.refetch();
    },
  };
}

// ── Mutation hooks ──────────────────────────────────────────

interface CreateRoleData {
  name: string;
  description?: string;
  level?: number;
  permissions?: string[];
}

/**
 * Create a new role
 */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRoleData) => {
      const response = await api.post<{ data: Role }>('/roles/definitions', data);
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...ROLES_QUERY_KEY, 'definitions'] });
      void queryClient.invalidateQueries({ queryKey: [...ROLES_QUERY_KEY, 'user-counts'] });
    },
  });
}

interface UpdateRoleData {
  name?: string;
  description?: string;
  level?: number;
}

/**
 * Update a role's metadata (name, description, level)
 */
export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, data }: { roleId: string; data: UpdateRoleData }) => {
      await api.patch(`/roles/definitions/${roleId}`, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...ROLES_QUERY_KEY, 'definitions'] });
    },
  });
}

/**
 * Bulk-set permissions for a role (replaces all existing permissions)
 */
export function useSetRolePermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, permissions }: { roleId: string; permissions: string[] }) => {
      const response = await api.put<{ success: boolean; permissionCount: number }>(
        `/roles/definitions/${roleId}/permissions`,
        { permissions }
      );
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...ROLES_QUERY_KEY, 'definitions'] });
    },
  });
}

/**
 * Delete a custom role
 */
export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleId: string) => {
      await api.delete(`/roles/definitions/${roleId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...ROLES_QUERY_KEY, 'definitions'] });
      void queryClient.invalidateQueries({ queryKey: [...ROLES_QUERY_KEY, 'user-counts'] });
    },
  });
}

/**
 * Get all permission categories in display order
 */
export function getPermissionCategories(): PermissionCategory[] {
  return ['dashboard', 'operations', 'customers', 'inventory', 'communication', 'reports', 'hr', 'administration'];
}
